import React from 'react';
import { Gift, ChevronRight, Download, Sparkles, Crown, Clock } from 'lucide-react';
import { RewardData } from '../hooks/useStatsData';
import { CardSkeleton } from './CardSkeleton';
interface CardRewardHubStatsProps {
  loading: boolean;
  data: RewardData | null;
}
export const CardRewardHubStats: React.FC<CardRewardHubStatsProps> = ({
  loading,
  data
}) => {
  if (loading) return <CardSkeleton data-id="element-20935" />;
  if (!data) return <div className="arcane-tablet bg-gradient-to-b from-[#0f0c29] to-[#302b63] rounded-lg border border-indigo-500/30 p-5 shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm" data-id="element-20936">
        <div className="flex items-center justify-between mb-4" data-id="element-20937">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-purple-200" data-id="element-20938">
            <Gift className="text-pink-400" size={18} data-id="element-20939" />
            Reward Hub Stats
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8" data-id="element-20940">
          <p className="text-indigo-200/70 text-sm" data-id="element-20941">
            No reward data available.
          </p>
        </div>
      </div>;
  // Calculate total for pie chart
  const sourceTotal = data.sourceBreakdown.rituals + data.sourceBreakdown.events + data.sourceBreakdown.purchases + data.sourceBreakdown.promos;
  // Calculate percentages for pie chart
  const ritualsPct = Math.round(data.sourceBreakdown.rituals / sourceTotal * 100);
  const eventsPct = Math.round(data.sourceBreakdown.events / sourceTotal * 100);
  const purchasesPct = Math.round(data.sourceBreakdown.purchases / sourceTotal * 100);
  const promosPct = Math.round(data.sourceBreakdown.promos / sourceTotal * 100);
  // Helper function to get source icon and color
  const getSourceInfo = (source: string) => {
    switch (source) {
      case 'lootbox':
        return {
          icon: <Gift size={14} data-id="element-20942" />,
          color: 'text-purple-300 bg-purple-900/30 border-purple-500/20'
        };
      case 'calendar':
        return {
          icon: <Clock size={14} data-id="element-20943" />,
          color: 'text-blue-300 bg-blue-900/30 border-blue-500/20'
        };
      case 'rituals':
        return {
          icon: <Crown size={14} data-id="element-20944" />,
          color: 'text-amber-300 bg-amber-900/30 border-amber-500/20'
        };
      case 'promo':
        return {
          icon: <Sparkles size={14} data-id="element-20945" />,
          color: 'text-pink-300 bg-pink-900/30 border-pink-500/20'
        };
      default:
        return {
          icon: <Gift size={14} data-id="element-20946" />,
          color: 'text-gray-300 bg-gray-900/30 border-gray-500/20'
        };
    }
  };
  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  return <div className="arcane-tablet bg-gradient-to-b from-indigo-50/90 to-purple-50/90 dark:from-[#0f0c29] dark:to-[#24243e] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-[0_0_15px_rgba(168,85,247,0.1)] dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm relative overflow-hidden" data-id="element-20947">
      {/* Mystical particle background */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-20948">
        {Array.from({
        length: 15
      }).map((_, i) => <div key={`particle-${i}`} className="absolute w-1 h-1 rounded-full bg-purple-500/20 dark:bg-purple-400/10 animate-float-particle" style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${Math.random() * 10 + 10}s`
      }} data-id="element-20949"></div>)}
      </div>

      {/* Decorative reward background */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10" data-id="element-20950">
        <svg width="100%" height="100%" className="absolute inset-0" data-id="element-20951">
          <pattern id="reward-pattern" width="50" height="50" patternUnits="userSpaceOnUse" data-id="element-20952">
            <path d="M25 10 L40 25 L25 40 L10 25 Z" fill="none" stroke="currentColor" strokeWidth="0.5" data-id="element-20953" />
            <circle cx="25" cy="25" r="15" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 2" data-id="element-20954" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#reward-pattern)" data-id="element-20955" />
        </svg>
      </div>

      {/* Glowing inner border */}
      <div className="absolute inset-0 rounded-lg border border-purple-300/20 dark:border-indigo-500/20 shadow-[inset_0_0_20px_rgba(168,85,247,0.05)] dark:shadow-[inset_0_0_30px_rgba(138,43,226,0.1)] pointer-events-none" data-id="element-20956"></div>

      <div className="relative" data-id="element-20957">
        <div className="flex items-center justify-between mb-4" data-id="element-20958">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-pink-300 dark:to-purple-200" data-id="element-20959">
            <Gift className="text-purple-600 dark:text-pink-400" size={18} data-id="element-20960" />
            Reward Hub Stats
          </h3>
        </div>

        {/* Lifetime value */}
        <div className="bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-pink-900/30 dark:to-purple-900/30 backdrop-blur-sm rounded-lg p-4 mb-4 border border-purple-200/50 dark:border-pink-500/20 shadow-[0_0_10px_rgba(168,85,247,0.05)] dark:shadow-[0_0_10px_rgba(236,72,153,0.1)]" data-id="element-20961">
          <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-pink-200" data-id="element-20962">
            Lifetime Value
          </h4>
          <div className="flex items-center gap-3" data-id="element-20963">
            <span className="text-xl font-bold text-pink-600 dark:text-pink-200 dark:etched-text" data-id="element-20964">
              {data.lifetime.tokens} 💗
            </span>
            <span className="text-gray-500 dark:text-indigo-200/70" data-id="element-20965">+</span>
            <span className="text-xl font-bold text-blue-600 dark:text-blue-200 dark:etched-text" data-id="element-20966">
              {data.lifetime.gems} 💎
            </span>
          </div>
        </div>

        {/* Stats overview */}
        <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-20967">
          <div className="bg-gray-100/80 dark:bg-gray-900/30 backdrop-blur-sm rounded-lg p-3 text-center border border-gray-200/70 dark:border-gray-500/20 shadow-[0_0_10px_rgba(168,85,247,0.05)] dark:shadow-[0_0_10px_rgba(156,163,175,0.1)]" data-id="element-20968">
            <p className="text-gray-500 dark:text-indigo-200/70 text-xs" data-id="element-20969">
              Total Claimed
            </p>
            <p className="font-medium text-gray-800 dark:text-indigo-200 dark:etched-text" data-id="element-20970">
              {data.totalClaimed}
            </p>
          </div>
          <div className="bg-purple-100/80 dark:bg-purple-900/30 backdrop-blur-sm rounded-lg p-3 text-center border border-purple-200/70 dark:border-purple-500/20 shadow-[0_0_10px_rgba(168,85,247,0.05)] dark:shadow-[0_0_10px_rgba(168,85,247,0.1)]" data-id="element-20971">
            <p className="text-purple-600 dark:text-purple-300 text-xs" data-id="element-20972">
              Skins Owned
            </p>
            <p className="font-medium text-purple-700 dark:text-purple-200 dark:etched-text" data-id="element-20973">
              {data.skinsOwned}
            </p>
          </div>
          <div className="bg-amber-100/80 dark:bg-amber-900/30 backdrop-blur-sm rounded-lg p-3 text-center border border-amber-200/70 dark:border-amber-500/20 shadow-[0_0_10px_rgba(251,191,36,0.05)] dark:shadow-[0_0_10px_rgba(251,191,36,0.1)]" data-id="element-20974">
            <p className="text-amber-600 dark:text-amber-300 text-xs" data-id="element-20975">
              VIP Days
            </p>
            <p className="font-medium text-amber-700 dark:text-amber-200 dark:etched-text" data-id="element-20976">
              {data.vipDays}
            </p>
          </div>
        </div>

        {/* Source breakdown */}
        <div className="mb-4" data-id="element-20977">
          <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-indigo-200" data-id="element-20978">
            Source Breakdown
          </h4>
          <div className="flex items-center gap-4" data-id="element-20979">
            {/* Cosmic pie chart visualization */}
            <div className="relative w-16 h-16" data-id="element-20980">
              <svg viewBox="0 0 100 100" className="w-full h-full dark:drop-shadow-[0_0_3px_rgba(138,43,226,0.5)]" data-id="element-20981">
                {/* Rituals segment */}
                <circle cx="50" cy="50" r="40" fill="none" stroke="#8B5CF6" strokeWidth="20" strokeDasharray={`${ritualsPct * 2.51} ${100 * 2.51 - ritualsPct * 2.51}`} strokeDashoffset="0" transform="rotate(-90 50 50)" className="dark:filter dark:drop-shadow-[0_0_2px_rgba(139,92,246,0.5)]" data-id="element-20982" />
                {/* Events segment */}
                <circle cx="50" cy="50" r="40" fill="none" stroke="#3B82F6" strokeWidth="20" strokeDasharray={`${eventsPct * 2.51} ${100 * 2.51 - eventsPct * 2.51}`} strokeDashoffset={`${-(ritualsPct * 2.51)}`} transform="rotate(-90 50 50)" className="dark:filter dark:drop-shadow-[0_0_2px_rgba(59,130,246,0.5)]" data-id="element-20983" />
                {/* Purchases segment */}
                <circle cx="50" cy="50" r="40" fill="none" stroke="#10B981" strokeWidth="20" strokeDasharray={`${purchasesPct * 2.51} ${100 * 2.51 - purchasesPct * 2.51}`} strokeDashoffset={`${-(ritualsPct + eventsPct) * 2.51}`} transform="rotate(-90 50 50)" className="dark:filter dark:drop-shadow-[0_0_2px_rgba(16,185,129,0.5)]" data-id="element-20984" />
                {/* Promos segment */}
                <circle cx="50" cy="50" r="40" fill="none" stroke="#EC4899" strokeWidth="20" strokeDasharray={`${promosPct * 2.51} ${100 * 2.51 - promosPct * 2.51}`} strokeDashoffset={`${-(ritualsPct + eventsPct + purchasesPct) * 2.51}`} transform="rotate(-90 50 50)" className="dark:filter dark:drop-shadow-[0_0_2px_rgba(236,72,153,0.5)]" data-id="element-20985" />
              </svg>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-2" data-id="element-20986">
              <div className="flex items-center gap-1.5" data-id="element-20987">
                <div className="w-3 h-3 bg-purple-500 rounded-full dark:shadow-[0_0_5px_rgba(139,92,246,0.5)]" data-id="element-20988"></div>
                <span className="text-xs text-purple-700 dark:text-purple-300" data-id="element-20989">
                  Rituals {ritualsPct}%
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-20990">
                <div className="w-3 h-3 bg-blue-500 rounded-full dark:shadow-[0_0_5px_rgba(59,130,246,0.5)]" data-id="element-20991"></div>
                <span className="text-xs text-blue-700 dark:text-blue-300" data-id="element-20992">
                  Events {eventsPct}%
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-20993">
                <div className="w-3 h-3 bg-green-500 rounded-full dark:shadow-[0_0_5px_rgba(16,185,129,0.5)]" data-id="element-20994"></div>
                <span className="text-xs text-green-700 dark:text-green-300" data-id="element-20995">
                  Purchases {purchasesPct}%
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-20996">
                <div className="w-3 h-3 bg-pink-500 rounded-full dark:shadow-[0_0_5px_rgba(236,72,153,0.5)]" data-id="element-20997"></div>
                <span className="text-xs text-pink-700 dark:text-pink-300" data-id="element-20998">
                  Promos {promosPct}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent claims */}
        <div className="mb-4" data-id="element-20999">
          <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-indigo-200" data-id="element-21000">
            Recent Claims
          </h4>
          <div className="space-y-2" data-id="element-21001">
            {data.recent.map(reward => {
            const {
              icon,
              color
            } = getSourceInfo(reward.source);
            return <div key={reward.id} className="flex items-center justify-between p-3 bg-white/70 dark:bg-gray-900/30 backdrop-blur-sm rounded-lg border border-gray-200/70 dark:border-gray-500/20 shadow-[0_0_10px_rgba(168,85,247,0.05)] dark:shadow-[0_0_10px_rgba(156,163,175,0.05)] relative overflow-hidden hover:shadow-[0_0_15px_rgba(168,85,247,0.1)] dark:hover:shadow-[0_0_15px_rgba(156,163,175,0.1)] transition-all duration-300 group" data-id="element-21002">
                  {/* Glowing left edge accent */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-400 to-pink-400 dark:from-purple-500 dark:to-pink-500 opacity-50 dark:opacity-70 group-hover:opacity-80 dark:group-hover:opacity-100 transition-opacity duration-300" data-id="element-21003"></div>
                  {/* Subtle glyph background pattern */}
                  <div className="absolute inset-0 opacity-5 dark:opacity-10 pointer-events-none" data-id="element-21004">
                    <svg width="100%" height="100%" data-id="element-21005">
                      <pattern id="glyph-pattern" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-21006">
                        <path d="M10 2 L18 10 L10 18 L2 10 Z" fill="none" stroke="currentColor" strokeWidth="0.5" data-id="element-21007" />
                      </pattern>
                      <rect width="100%" height="100%" fill="url(#glyph-pattern)" data-id="element-21008" />
                    </svg>
                  </div>
                  <div className="flex items-center gap-3 relative" data-id="element-21009">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-sm ${color} group-hover:shadow-[0_0_10px_rgba(168,85,247,0.2)] transition-shadow duration-300`} data-id="element-21010">
                      {icon}
                    </div>
                    <div data-id="element-21011">
                      <p className="text-sm font-medium text-gray-800 dark:text-indigo-200" data-id="element-21012">
                        {reward.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-indigo-200/70" data-id="element-21013">
                        {formatDate(reward.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm font-medium relative" data-id="element-21014">
                    {reward.amount}{' '}
                    {reward.type === 'tokens' ? '💗' : reward.type === 'gems' ? '💎' : reward.type === 'vip' ? '👑' : '✨'}
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-purple-200/30 dark:border-indigo-500/20 flex gap-2" data-id="element-21015">
          <button className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-pink-500 dark:from-pink-600 dark:to-purple-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-purple-500/20 dark:hover:shadow-pink-500/20 transition-all flex items-center justify-center gap-1 border border-purple-400/30 dark:border-pink-500/30 shadow-[0_0_10px_rgba(168,85,247,0.1)] dark:shadow-[0_0_10px_rgba(236,72,153,0.2)] relative overflow-hidden group" onClick={() => {
          // Navigate to reward hub
          const event = new CustomEvent('navigate', {
            detail: {
              section: 'rewards'
            }
          });
          document.dispatchEvent(event);
        }} data-id="element-21016">
            {/* Button hover glow effect */}
            <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-21017"></div>
            <span className="relative z-10" data-id="element-21018">Go to Reward Hub</span>
            <ChevronRight size={16} className="relative z-10" data-id="element-21019" />
          </button>
          <button className="px-3 py-2 bg-gray-100/80 dark:bg-gray-900/50 backdrop-blur-sm rounded-lg text-sm font-medium text-gray-700 dark:text-indigo-200 border border-gray-200/70 dark:border-gray-500/20 hover:bg-gray-200/80 dark:hover:bg-gray-800/50 transition-all flex items-center justify-center group" data-id="element-21020">
            <Download size={16} className="group-hover:text-purple-500 dark:group-hover:text-pink-400 transition-colors" data-id="element-21021" />
          </button>
        </div>
      </div>
    </div>;
};